/**
 * Body Data Service
 * Manages body data (weight, BMI) and its history
 */

import { api } from "./api";
import { log } from "@/lib/log";
import { useI18nStore } from "@/stores/i18nStore";
import { formatChartDate } from "@/utils/dateLocalization";

export interface BodyDataEntry {
  id?: string;
  weight: number;
  unit: "kg" | "lb";
  bmi?: number;
  recordedAt: string;
  userId: string;
}

export interface BodyDataHistoryItem {
  date: string;
  weight: number;
  bmi?: number;
  bodyFat?: number;
  timestamp: string;
}

export interface BodyDataResponse {
  success: boolean;
  data: {
    latestBodyData?: BodyDataEntry;
    history?: BodyDataHistoryItem[];
    message?: string;
  };
  meta: {
    timestamp: string;
    version: string;
  };
}

class BodyDataService {
  /**
   * Save new body data
   */
  async saveBodyData(data: {
    weight?: number;
    unit: "kg" | "lb";
    height?: number;
    heightUnit?: "cm" | "in";
    bodyFat?: number;
    bodyFatUnit?: "%" | "percent";
  }): Promise<BodyDataResponse> {
    try {
      const response = await api.post("/body-data", data);
      return response.data;
    } catch (error: any) {
      console.error("Error saving body data:", error);
      throw error;
    }
  }

  /**
   * Load the latest body data
   */
  async getLatestBodyData(): Promise<unknown> {
    try {
      const response = await api.get("/body-data/latest");

      if (response.data?.success && response.data.data) {
        log.debug("✅ Latest body data from FHIR backend:", response.data.data);
      } else {
        log.debug("ℹ️ No latest body data found in FHIR database");
      }

      return response.data;
    } catch (error: any) {
      console.error("Error fetching latest body data:", error);
      throw error;
    }
  }

  /**
   * Load the body data history for charts
   * ✅ CRITICAL: Only returns REAL FHIR data from backend API
   * NEVER generates or returns mock/synthetic data
   */
  async getBodyDataHistory(
    months: number = 12,
  ): Promise<BodyDataHistoryItem[]> {
    try {
      const response = await api.get("/body-data/history", {
        params: { days: months * 30 }, // Convert months to days for API
      });

      if (response.data?.success && response.data.data?.history) {
        // Transform backend data to frontend format
        const historyData = response.data.data.history.map(
          (item: {
            recordedAt: string;
            type: string;
            value: number;
            unit?: string;
          }) => ({
            date: item.recordedAt,
            weight: item.type === "weight" ? item.value : null,
            bmi: item.type === "bmi" ? item.value : null,
            bodyFat: item.type === "bodyFat" ? item.value : null,
            timestamp: item.recordedAt,
          }),
        );

        return historyData;
      }

      return []; // Return empty array for no data - NOT mock data
    } catch (error: any) {
      console.error(
        "❌ Error fetching body data history from FHIR backend:",
        error,
      );
      return []; // Return empty array on error - NEVER mock data
    }
  }

  /**
   * Calculate BMI
   */
  calculateBMI(
    weight: number,
    weightUnit: "kg" | "lb",
    height: number,
    heightUnit: "cm" | "in",
  ): number {
    // Convert to metric units
    const weightKg = weightUnit === "kg" ? weight : weight * 0.453592;
    const heightM = heightUnit === "cm" ? height / 100 : height * 0.0254;

    if (heightM === 0) return 0;

    return Math.round((weightKg / (heightM * heightM)) * 10) / 10;
  }

  /**
   * Format a date for chart display, in the app language
   * @deprecated Use formatChartDate from utils/dateLocalization.ts instead
   */
  formatChartDate(date: string): string {
    return formatChartDate(date, useI18nStore.getState().language);
  }

  /**
   * ❌ REMOVED: Mock data generation
   * This function was removed to prevent showing fake data instead of real FHIR data.
   * Body data charts should only show actual user entries from the database.
   *
   * If no real data exists, the chart shows an empty state with:
   * "No weight data available"
   */
}

export const bodyDataService = new BodyDataService();
